import { compare, valid } from "semver";
import { ENV_OFFLINE, ENV_SKIP_VERSION_CHECK, getEnvValue, PACKAGE_NAME } from "../config.js";
import { fetchWithRetry } from "./management-http.ts";

const LATEST_VERSION_URL = `https://registry.npmjs.org/${PACKAGE_NAME}/latest`;
const DEFAULT_VERSION_CHECK_TIMEOUT_MS = 10000;

/**
 * The versionless placeholder stamped on `main` and read from source-tree dev
 * runs (`bun packages/coding-agent/src/cli.ts`). Real releases never carry it —
 * `scripts/cut-release.ts` materializes the actual version on the tag commit —
 * so encountering it means this is a dev build that should not be compared
 * against the published registry version.
 */
const DEV_VERSION_PLACEHOLDER = "0.0.0";

export function isDevVersion(version: string): boolean {
	return version.trim() === DEV_VERSION_PLACEHOLDER;
}

export interface LatestPiRelease {
	version: string;
	packageName?: string;
	note?: string;
}

/** Include useful errno details hidden behind Node's generic "fetch failed" error. */
export function formatVersionCheckError(error: unknown): string {
	const rootMessage = error instanceof Error && error.message ? error.message : String(error);
	const cause = error instanceof Error ? error.cause : undefined;
	const causes = cause instanceof AggregateError ? cause.errors : cause === undefined ? [] : [cause];
	const codes = causes
		.map((value) =>
			typeof value === "object" && value !== null && "code" in value && typeof value.code === "string"
				? value.code
				: undefined,
		)
		.filter((code): code is string => code !== undefined);

	if (codes.length > 0) return `${rootMessage} (${[...new Set(codes)].join(", ")})`;
	const causeMessage = causes.find(
		(value): value is Error => value instanceof Error && Boolean(value.message),
	)?.message;
	return causeMessage ? `${rootMessage} (cause: ${causeMessage})` : rootMessage;
}

export function comparePackageVersions(leftVersion: string, rightVersion: string): number | undefined {
	const left = valid(leftVersion.trim());
	const right = valid(rightVersion.trim());
	if (!left || !right) {
		return undefined;
	}
	return compare(left, right);
}

export function isNewerPackageVersion(candidateVersion: string, currentVersion: string): boolean {
	const comparison = comparePackageVersions(candidateVersion, currentVersion);
	if (comparison !== undefined) {
		return comparison > 0;
	}
	return candidateVersion.trim() !== currentVersion.trim();
}

export async function getLatestPiRelease(
	options: { timeoutMs?: number; retry?: boolean } = {},
): Promise<LatestPiRelease | undefined> {
	if (getEnvValue(ENV_OFFLINE)) return undefined;

	const response = await fetchWithRetry(
		LATEST_VERSION_URL,
		{
			headers: {
				accept: "application/json",
			},
		},
		{
			maxRetries: options.retry ? 2 : 0,
			timeoutMs: options.timeoutMs ?? DEFAULT_VERSION_CHECK_TIMEOUT_MS,
		},
	);
	if (!response.ok) return undefined;

	const data = (await response.json()) as { name?: unknown; version?: unknown; note?: unknown };
	if (typeof data.version !== "string" || !data.version.trim()) return undefined;
	const packageName = typeof data.name === "string" && data.name.trim() ? data.name.trim() : undefined;
	const note = typeof data.note === "string" && data.note.trim() ? data.note.trim() : undefined;
	return { version: data.version.trim(), packageName, ...(note ? { note } : {}) };
}

export async function getLatestPiVersion(
	options: { timeoutMs?: number; retry?: boolean } = {},
): Promise<string | undefined> {
	return (await getLatestPiRelease(options))?.version;
}

export async function checkForNewPiVersion(currentVersion: string): Promise<string | undefined> {
	if (getEnvValue(ENV_SKIP_VERSION_CHECK)) return undefined;
	// Dev builds always read the versionless `0.0.0` placeholder, which is older
	// than any published release, so the registry check would always nag. Skip it
	// (and the network call) for source-tree/dev runs.
	if (isDevVersion(currentVersion)) {
		return undefined;
	}
	try {
		const latestVersion = await getLatestPiVersion();
		if (latestVersion && isNewerPackageVersion(latestVersion, currentVersion)) {
			return latestVersion;
		}
		return undefined;
	} catch {
		return undefined;
	}
}
